import { RootState } from "redux/store";
import { Modal, Row, Col } from "react-bootstrap";
import { useEffect, useState, useCallback } from "react";
import { PopupTopBar, Table, ProTable } from "components";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import classNames from "classnames";
import { cleanGenericCategoriesDetails, getGenericCategories, updateGenericCategoriesField, addDeselectedRows, addSelectedRows } from "redux/slices/GenericCategories";
import { getProducts } from "redux/slices/Products";


type GenericCategoriesStocksProps = {
	show: boolean;
	onClose: () => void;
	onSave: () => void;
}

export function GenericCategoriesStocks(props: GenericCategoriesStocksProps) {
	const dispatch = useAppDispatch();
	const [saving, setSaving] = useState(false);
	const [selectedId, setSelectedId] = useState(0);
	const [selectedGenericCategory, setSelectedGenericCategory] = useState("");
	const [genericCategoriesList, setGenericCategoriesList] = useState<any[]>([]);
	const genericCategories = useAppSelector((state: RootState) => state.genericCategories);
	const catalogue = useAppSelector((state: RootState) => state.catalogues);
	const [counterSelectedRows, setCounterSelectedRows] = useState<number>(0);

	const columnsa = [
		{
			Header: "Generic Category",
			accessor: "fullName",
			width: 150,
			Cell: (row: any) => {
				const id: number = parseInt(row.cell.row.id);
				return ( <div className={classNames("ps-1", "pe-3", id === selectedId ? "selected-cell" : "deselected-cell")}
						onClick={() => {
							setSelectedId(id);
							setSelectedGenericCategory(row.cell.row.original.id);
							setGenericCategoriesTable(row.cell.row.original.id);
						}}
					>{row.value}</div>
				);
			},
		},
	];

	const columnsb = [
		{
			Header: "Name",
			accessor: "name",
			width: 700,
		},
	];

	useEffect(() => {
		if (props.show) {
			setSaving(false);
			dispatch(cleanGenericCategoriesDetails());
			dispatch(getGenericCategories({
				keyword: "",
				offset: 0,
				limit: 100,
			})).then((res) => {
				if(res.data.length > 0) setGenericCategoriesTable(res.data[0].id);
			});
		} else {
			setSelectedId(0);
		}
	}, [props.show]);

	function setGenericCategoriesTable(selectedGenericCategory: string) {
		dispatch(getProducts({
			genericCategoryIds: [selectedGenericCategory]
		})).then((response) => {
			setGenericCategoriesList(response.data);
			handleChange("stockList", response.data);
		});
	}

	useEffect(() => {
		let rows = 0;
		if (!genericCategories.selectedRows) setCounterSelectedRows(0);
		if (genericCategories.selectAllFilter) {
			let deselectedRows = 0;
			genericCategories.deselectedRows.forEach((row: any) => deselectedRows = deselectedRows + row.length);
			rows = genericCategories.totalRows - deselectedRows;
		} else {
			genericCategories.selectedRows.forEach((row: any) => rows = rows + row.length);
		}
		setCounterSelectedRows(rows);
	}, [genericCategories.selectedRows, genericCategories.deselectedRows]);

	useEffect(() => {
		columnCheckDeselectAll();
	}, [genericCategories.pageSize]);

	function onSearchClick(keyword: string) {
		handleChange("keyword", keyword);
		dispatch(updateGenericCategoriesField({ key: "pageIndex", value: 0 }));
		columnCheckDeselectAll();
		dispatch(getGenericCategories({
			keyword: keyword,
			offset: 0,
			limit: genericCategories.pageSize,
		})).then((res) => {
			if(res.data.length > 0) setGenericCategoriesTable(res.data[0].id as string);
		});
	}

	function handleChange(option: any, optionValue: any) {
		dispatch(updateGenericCategoriesField({ key: option, value: optionValue }));
	}

	function updateTableRows(dataTableState: any) {
		dispatch(updateGenericCategoriesField({ key: "pageIndex", value: dataTableState.pageIndex }));
		dispatch(updateGenericCategoriesField({ key: "pageSize", value: dataTableState.pageSize }));
		dispatch(getGenericCategories({
			keyword: catalogue.keyword,
			limit: dataTableState.pageSize,
			offset: dataTableState.pageIndex * dataTableState.pageSize,
		})).then((res) => {
			if(res.data.length > 0) setGenericCategoriesTable(res.data[0].id as string);
		});
	}

	function deselectedRowsUpdate(rows: any, index: any) {
		dispatch(addDeselectedRows({ rows: rows, index: index }));
	}

	function selectedRowsUpdate(rows: any, index: any) {
		dispatch(addSelectedRows({ rows: rows, index: index }));
	}

	const selectedRows = useCallback((pageIndex:any) => {
		handleChange("selectedgenericCategoriesStock", pageIndex);
    }, []);

	function columnCheckDeselectAll() {
		handleChange("selectAllFilter", false);
		handleChange("deselectedRows", []);
		handleChange("selectedRows", []);
	}

	function columnCheckSelectAll() {
		handleChange("selectAllFilter", true);
		handleChange("deselectedRows", []);
	}

	function saveSelectedRows() {
		// setSaving(true);
		props.onSave();
	}

	return (
		<Modal size="xl" centered show={props.show} onHide={props.onClose}>
			<Modal.Header closeButton>
				<Modal.Title>System Stocks by Generic Category</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<Row>
					<Col xs={6}>
						<ProTable
							data={genericCategories}
							pageSize={100}
							isSortable={true}
							pagination={true}
							disableExport={true}
							columns={columnsa}
							table={genericCategories.table}
							loading={genericCategories.loading}
							updateTableRows={updateTableRows}
							className="table-text-uppercase"
							// Search Props
							isSearchable={true}
							onSearchClick={onSearchClick}
							// Select Props
							isSelectable={false}
							selectAllFilter={genericCategories.selectAllFilter}
							selectedRows={genericCategories.selectedRows}
							deselectedRows={genericCategories.deselectedRows}
							selectedRowsUpdate={selectedRowsUpdate}
							deselectedRowsUpdate={deselectedRowsUpdate}
							columnCheckSelectAll={columnCheckSelectAll}
							columnCheckDeselectAll={columnCheckDeselectAll}
						// Extra Components Props
						// topbarChild={<PopupTopBar counter={counterSelectedRows} onSave={saveSelectedRows} saving={saving} />}
						/>
					</Col>
					<Col xs={6}>
						<Table
							pageSize={15}
							isSortable={true}
							pagination={true}
							isSelectable={true}
							disableExport={true}
							columns={columnsb}
							selectedRows={selectedRows}
							loading={genericCategories.loading}
							data={genericCategories.stockList}
							className="generic-category-table"
							// Extra Components Props
							topbarChild={<PopupTopBar counter={genericCategories.selectedgenericCategoriesStock.length} onSave={saveSelectedRows} saving={saving} />}
						/>
					</Col>
				</Row>
			</Modal.Body>
		</Modal>
	);
}